<template>
  <div>
    <div @click="toggleNotification">
      <ElBadge :hidden="unreadNotificationsCount <= 0" :value="getUnreadNumber" class="cursor-pointer">
        <WcIconBell />
      </ElBadge>
    </div>
    <div
      class="fixed w-full left-0 bg-transparent transition-all transition-duration-500"
      :style="{maxHeight: '95vh', height: showInbox ? '100%' : '0', zIndex: 10}"
      @click="toggleNotification">
      <div
        @click.stop
        :class="`fixed top-14 right-0 bg-white border-gray-400 overflow-hidden shadow-2xl transition-all transition-duration-500 ${showInbox? 'py-2' : ''}`"
        :style="{maxHeight: '95vh', height: showInbox ? '95%' : '0', width: '30%', zIndex: 11}">
        <div class="px-4 py-2 border-b flex gap-4 justify-between align-items-center">
          <WcTypography title="Notifications" size="sm"  weight="500" />
          <div class="flex gap-2 align-items-center">
            <WcTooltip :text="t('bulkActions')">
              <InboxActions @refresh="refresh" />
            </WcTooltip>
            <PrimaryFilter :query="query" @handle-change="refresh" />
            <WcTooltip :text="t('preferences')">
              <WcATag link='recruiter-settings/notification-preferences' target="_blank">
                <WcIconSetting />
              </WcATag>
            </WcTooltip>
            <WcTooltip :text="$t('general.close')">
              <div class="p-1 cursor-pointer" @click="toggleNotification">
                <WcIconCross width="14" height="14"  color="#09121F"  />
              </div>
            </WcTooltip>
          </div>
        </div>
        <div class="flex justify-center gap-2 p-3">
          <ElRadioGroup v-model="query.filter" @change="refresh" class="flex gap-2" >
            <ElRadio :label="'UNREAD'">{{ t('unread') }}</ElRadio>
            <ElRadio :label="'STARRED'" >{{ t('starred') }}</ElRadio>
            <ElRadio :label="'ALL'" >{{ t('all') }}</ElRadio>
          </ElRadioGroup>
        </div>
        <div v-if="actionLoading" class="flex stretch flex-col h-full">
          <InboxLoading />
        </div>
        <div v-else-if="inboxNotifications.length === 0 && !actionLoading"  class="flex-col h-80 w-full align-items-center justify-center p-4 gap-2">
          <WcIconSearch class="w-10 h-10"/>
          <WcTypography :title="getEmptyNotificationsTitle" size="smd"  weight="600" />
          <WcTypography :title="getEmptyNotificationBody" size="xs" color="rgb(4,4,4,0.47)" />
          <WcTypography v-if="query.filter !== InboxFilterEnum.STARRED" :title="$t('homePage.inbox.uptoDate')" size="xs" color="rgb(4,4,4,0.47)" />
        </div>
        <div v-else class="relative overflow-y-auto scroll-w-1" @scroll.stop style="max-height: 80vh; height: 80%">
          <InboxNotification
            v-for="each in inboxNotifications"
            class="eachNotification"
            :key="each.id"
            :inbox-notification="each"
            @deleted="deletedNotification"
            @read="readNotification"
          />
          <div class="w-full flex p-2 justify-center" v-if="paginationOptions.hasNext">
            <Button type="outline" plane @click="loadMore" :loading="loading">{{$t('general.loadMore')}}</Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { isMobile } from '@/utils/general'
import InboxNotificationsResource from '@/api/inbox/inboxNotifications'
import { InboxEntity, InboxNotificationResponse, InboxQuery, PaginationOptions } from './models/inbox.entity'
import { handleError } from '@/utils/request'
import WcTypography from '@/components/UILibrary/Typography/WcTypography.vue'
import InboxNotification from '@/views/home-page/inbox/components/InboxNotification.vue'
import PrimaryFilter from '@/views/home-page/inbox/components/PrimaryFilter.vue'
import WcIconSearch from '@/components/UILibrary/Icons/WcIconSearch.vue'
import InboxActions from '@/views/home-page/inbox/components/InboxActions.vue'
import WcIconBell from '@/components/UILibrary/Icons/WcIconBell.vue'
import { InboxActionsEnum, InboxFilterEnum } from '@/views/home-page/inbox/models/inbox-type.enum'
import WcIconSetting from '@/components/UILibrary/Icons/WcIconSetting.vue'
import WcATag from '@/components/UILibrary/Typography/WcATag.vue'
import InboxLoading from '@/views/home-page/inbox/components/InboxLoading.vue'
import WcTooltip from '@/components/UILibrary/WcTooltip.vue'
import WcIconCross from '@/components/UILibrary/Icons/WcIconCross.vue'
import Button from '@/components/UILibrary/Buttons/Button.vue'

@Component({
  name: 'Inbox',
  computed: {
    InboxFilterEnum() {
      return InboxFilterEnum
    },
    InboxActionsEnum() {
      return InboxActionsEnum
    }
  },
  methods: { isMobile },
  components: {
    Button,
    WcIconCross,
    WcTooltip,
    InboxLoading,
    WcATag,
    WcIconSetting,
    WcIconBell,
    InboxActions,
    WcIconSearch,
    PrimaryFilter,
    InboxNotification,
    WcTypography
  }
})
export default class Inbox extends Vue {
  inboxNotificationsResource = new InboxNotificationsResource()
  inboxNotifications: InboxEntity[] = []
  loading: boolean = false
  actionLoading: boolean = false
  showInbox = false
  unreadNotificationsCount = 0;
  query: InboxQuery = { order: 'DESC', filter: InboxFilterEnum.UNREAD, page: 1, perPage: 20 }
  paginationOptions: PaginationOptions = { page: 1, perPage: 20, total: null, totalPages: null, hasNext: false }

  async mounted(): Promise<void> {
    await this.getUnreadNotificationCount()
  }

  async getUnreadNotificationCount(): Promise<void> {
    try {
      this.unreadNotificationsCount = await this.inboxNotificationsResource.listCount(InboxFilterEnum.UNREAD)
    } catch (e) {
      handleError(e, this.$message)
    }
  }

  get getUnreadNumber(): string {
    return this.unreadNotificationsCount > 99 ? '99+' : this.unreadNotificationsCount.toString()
  }

  async getInboxNotifications(append: boolean = false): Promise<void> {
    this.loading = true
    try {
      const data = (await this.inboxNotificationsResource.list(this.query)).data as InboxNotificationResponse
      if (append) {
        this.inboxNotifications.push(...data.notifications)
      } else {
        this.inboxNotifications = data.notifications
      }
      this.paginationOptions = data.paginationOptions as PaginationOptions
    } catch (e) {
      handleError(e, this.$message)
    } finally {
      this.loading = false
    }
  }

  loadMore(): void {
    if (this.paginationOptions.hasNext) {
      this.query.page = this.query.page + 1
      this.getInboxNotifications(true)
    }
  }

  deletedNotification(deletedNotificationId: string): void {
    const notification = this.inboxNotifications.find((each) => each.id === deletedNotificationId)
    if (notification && !notification.isRead) {
      this.readNotification(true)
    }
    this.inboxNotifications = this.inboxNotifications.filter((each) => each.id !== deletedNotificationId)
  }

  readNotification(isRead: boolean): void {
    this.unreadNotificationsCount = Math.max(this.unreadNotificationsCount + (isRead ? -1 : 1), 0)
  }

  initializePagination(): void {
    this.query.page = 1
  }

  async refresh(): Promise<void> {
    this.actionLoading = true
    this.initializePagination()
    await this.getInboxNotifications()
    await this.getUnreadNotificationCount()
    this.actionLoading = false
  }

  async toggleNotification(): Promise<void> {
    this.showInbox = !this.showInbox
    if (this.showInbox) {
      this.actionLoading = true
      await this.getInboxNotifications()
      this.actionLoading = false
    }
  }

  get getEmptyNotificationsTitle(): string {
    if (this.query.filter === InboxFilterEnum.UNREAD) {
      return this.$t('homePage.inbox.emptyUnread').toString()
    } else if (this.query.filter === InboxFilterEnum.STARRED) {
      return this.$t('homePage.inbox.emptyStarred').toString()
    }
    return this.$t('homePage.inbox.emptyAll').toString()
  }

  get getEmptyNotificationBody(): string {
    if (this.query.filter === InboxFilterEnum.UNREAD) {
      return this.$t('homePage.inbox.emptyUnreadDescription').toString()
    } else if (this.query.filter === InboxFilterEnum.STARRED) {
      return this.$t('homePage.inbox.emptyStarredDescription').toString()
    }
    return this.$t('homePage.inbox.emptyInboxDescription').toString()
  }

  t(key: string): string {
    return this.$t(`homePage.inbox.${key}`).toString()
  }
}
</script>
<style scoped>
.notifications .eachNotification {
  border-bottom: 1px solid #e9e9ec;
}
.notifications .eachNotification:last-child {
  border-bottom: none;
}

::v-deep .el-badge__content.is-fixed {
  font-size: 11px !important;
}
::v-deep .el-badge__content{
  padding: 0 5px 0 5px !important;
  display: flex;
  align-items: center;
}

[dir="rtl"] .right-0 {
  left: 0;
  right: unset;
}
[dir="ltr"] .right-0 {
  right: 0;
  left: unset;
}
</style>
